'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import PickerCircle from './PickerCircle';
import { hsvToRgb, rgbToHex, getHueColor } from './colorUtils';
const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '238px',
    flexShrink: 0,
    position: 'relative',
    cursor: 'crosshair',
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '180px'
    }
}));
const TopRightCornerColor = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '238px',
    flexShrink: 0,
    borderRadius: '8px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    position: 'absolute',
    left: 0,
    top: 0,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '180px'
    }
}));
const SaturationOverlay = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '238px',
    flexShrink: 0,
    borderRadius: '8px',
    background: 'linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0) 100%)',
    position: 'absolute',
    left: 0,
    top: 0,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '180px'
    }
}));
const BrightnessOverlay = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '238px',
    flexShrink: 0,
    borderRadius: '8px',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 100%)',
    position: 'absolute',
    left: 0,
    top: 0,
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: '180px'
    }
}));
const StyledPickerCircle = styled(PickerCircle)(({ x, y, theme }) => ({
    left: `${x - 16}px`,
    top: `${y - 16}px`,
    cursor: 'grab',
    '&:active': {
        cursor: 'grabbing'
    },
    [theme.breakpoints.down('sm')]: {
        left: `${x - 12}px`,
        top: `${y - 12}px`
    }
}));
const ColorPickerArea = ({ currentColor, onChange, pickerPosition, onPositionChange }) => {
    const containerRef = useRef(null);
    const handleMouseDown = useCallback((event) => {
        if (!containerRef.current)
            return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(319, event.clientX - rect.left));
        const y = Math.max(0, Math.min(238, event.clientY - rect.top));
        const saturation = Math.round((x / 319) * 100);
        const brightness = Math.round(((238 - y) / 238) * 100);
        const rgb = hsvToRgb(currentColor.hsv.h, saturation, brightness);
        const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
        onPositionChange({ x, y });
        onChange?.({
            hex,
            rgb,
            hsv: { h: currentColor.hsv.h, s: saturation, v: brightness }
        });
        const handleMouseMove = (moveEvent) => {
            const newX = Math.max(0, Math.min(319, moveEvent.clientX - rect.left));
            const newY = Math.max(0, Math.min(238, moveEvent.clientY - rect.top));
            const newSaturation = Math.round((newX / 319) * 100);
            const newBrightness = Math.round(((238 - newY) / 238) * 100);
            const newRgb = hsvToRgb(currentColor.hsv.h, newSaturation, newBrightness);
            const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
            onPositionChange({ x: newX, y: newY });
            onChange?.({
                hex: newHex,
                rgb: newRgb,
                hsv: {
                    h: currentColor.hsv.h,
                    s: newSaturation,
                    v: newBrightness
                }
            });
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [currentColor.hsv.h, onChange, onPositionChange]);
    const hueColor = getHueColor(currentColor.hsv.h);
    return (_jsxs(Container, { ref: containerRef, onMouseDown: handleMouseDown, children: [_jsx(TopRightCornerColor, { sx: { backgroundColor: hueColor } }), _jsx(SaturationOverlay, {}), _jsx(BrightnessOverlay, {}), _jsx(StyledPickerCircle, { color: currentColor.hex, className: "picker-circle-main", x: pickerPosition.x, y: pickerPosition.y })] }));
};
export default ColorPickerArea;
